import * as m from '@staiter/i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { RefreshCw, Upload } from 'lucide-react';
import { useState } from 'react';
import { MasterDriftResolver } from '~/components/conflicts';
import { PageHeader, Table } from '~/components/data';
import { Button, Drawer, Select } from '~/components/primitives';
import {
  type SyncStagingFilter,
  fetchSyncStaging,
  triggerOnecPullMaster,
} from '~/lib/api/sync-staging';
import shared from './_auth._shared.module.css';
import styles from './_auth.admin.module.css';

export const Route = createFileRoute('/_auth/admin/sync-staging')({
  staticData: { minMainWidth: 960 },
  component: SyncStagingAdmin,
});

function SyncStagingAdmin(): JSX.Element {
  const qc = useQueryClient();
  const [filter, setFilter] = useState<SyncStagingFilter>({ resolution: 'unresolved' });
  const [selectedConflictId, setSelectedConflictId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'sync-staging', filter],
    queryFn: () => fetchSyncStaging(filter),
  });

  const pullMut = useMutation({
    mutationFn: triggerOnecPullMaster,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['admin', 'sync-staging'] }),
  });

  return (
    <div className={shared.page}>
      <PageHeader
        title={m.route_admin_sync_staging_title()}
        description={m.route_admin_sync_staging_description()}
        actions={
          <>
            <Button
              variant="secondary"
              size="sm"
              leadingIcon={<RefreshCw size={14} />}
              onClick={() => pullMut.mutate()}
              disabled={pullMut.isPending}
            >
              {m.route_admin_sync_staging_btn_pull()}
            </Button>
            {/* F91 (Ф6): отправка мастер-данных — заявкой уровня 3 на экране «Финансы». */}
            <Link to="/finance/onec-push" className={styles.movedLink}>
              <Upload size={14} aria-hidden="true" /> {m.admin_onec_push_moved_link()}
            </Link>
          </>
        }
      />

      <section className={shared.sectionCard}>
        <h3 className={shared.sectionHeading}>{m.route_admin_sync_staging_filters_heading()}</h3>
        <div className={shared.formGrid3}>
          <Select
            label={m.route_admin_sync_staging_filter_source()}
            value={filter.source ?? ''}
            onChange={(e) => setFilter((f) => ({ ...f, source: e.target.value || undefined }))}
            options={[
              { value: '', label: m.route_admin_sync_staging_filter_all() },
              { value: '1c', label: m.route_admin_sync_staging_source_1c() },
              { value: 'bitrix', label: m.route_admin_sync_staging_source_bitrix() },
              { value: 'adesk', label: m.route_admin_sync_staging_source_adesk() },
              { value: 'excel', label: m.route_admin_sync_staging_source_excel() },
            ]}
          />
          <Select
            label={m.route_admin_sync_staging_filter_resource()}
            value={filter.resource ?? ''}
            onChange={(e) => setFilter((f) => ({ ...f, resource: e.target.value || undefined }))}
            options={[
              { value: '', label: m.route_admin_sync_staging_filter_all() },
              { value: 'item', label: m.route_admin_sync_staging_resource_item() },
              { value: 'warehouse', label: m.route_admin_sync_staging_resource_warehouse() },
              { value: 'batch', label: m.route_admin_sync_staging_resource_batch() },
              { value: 'bom', label: m.route_admin_sync_staging_resource_bom() },
              { value: 'counterparty', label: m.route_admin_sync_staging_resource_counterparty() },
            ]}
          />
          <Select
            label={m.route_admin_sync_staging_filter_resolution()}
            value={filter.resolution ?? ''}
            onChange={(e) => setFilter((f) => ({ ...f, resolution: e.target.value || undefined }))}
            options={[
              { value: '', label: m.route_admin_sync_staging_filter_all() },
              { value: 'unresolved', label: m.route_admin_sync_staging_resolution_unresolved() },
              { value: 'ours', label: m.route_admin_sync_staging_resolution_ours() },
              { value: 'theirs', label: m.route_admin_sync_staging_resolution_theirs() },
              { value: 'merged', label: m.route_admin_sync_staging_resolution_merged() },
              { value: 'escalated', label: m.route_admin_sync_staging_resolution_escalated() },
            ]}
          />
        </div>
      </section>

      <Table
        ariaLabel={m.route_admin_sync_staging_table_aria()}
        loading={isLoading}
        data={data?.items ?? []}
        rowKey={(r) => r.id}
        columns={[
          {
            id: 'source',
            header: m.route_admin_sync_staging_filter_source(),
            accessor: (r) => r.source,
            mono: true,
          },
          {
            id: 'resource',
            header: m.route_admin_sync_staging_filter_resource(),
            accessor: (r) => r.resource,
            mono: true,
          },
          {
            id: 'op',
            header: m.route_admin_sync_staging_col_operation(),
            accessor: (r) => r.operation,
            mono: true,
          },
          {
            id: 'subject',
            header: m.route_admin_sync_staging_col_subject(),
            accessor: (r) => r.subject,
          },
          {
            id: 'detected',
            header: m.route_admin_sync_staging_col_detected(),
            accessor: (r) => new Date(r.detected_at).toLocaleString(),
            mono: true,
          },
          {
            id: 'res',
            header: m.route_admin_sync_staging_filter_resolution(),
            accessor: (r) => r.resolution,
            mono: true,
          },
          {
            id: 'actions',
            header: '',
            accessor: (r) =>
              r.conflict_id ? (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setSelectedConflictId(r.conflict_id)}
                >
                  {m.route_admin_sync_staging_btn_resolve()}
                </Button>
              ) : (
                <span className={shared.subtle}>—</span>
              ),
          },
        ]}
      />

      <Drawer
        open={selectedConflictId !== null}
        onClose={() => setSelectedConflictId(null)}
        title={m.route_admin_sync_staging_drawer_title()}
        width={640}
      >
        {selectedConflictId && (
          <MasterDriftResolver
            conflictId={selectedConflictId}
            onResolved={() => {
              setSelectedConflictId(null);
              void qc.invalidateQueries({ queryKey: ['admin', 'sync-staging'] });
            }}
          />
        )}
      </Drawer>
    </div>
  );
}
